import React, { PureComponent } from 'react'
import PropTypes from 'prop-types'
import { reduxForm, formValues } from 'redux-form'
import { RadioButtonWithPrice, RadioButtonGroup } from '@ynap/radio-button'
import Field from '@ynap/field'
import BEMHelper from '@ynap/bem-helper'
import { SimpleAsyncButton, Button } from '@ynap/button'
import Checkbox from '@ynap/checkbox'
import { submit } from '@ynap/form-utils'
import collateErrors from './collateErrors'
import normalizeGiftmessage from './normalizeGiftMessage'
import { Tooltip, Notification } from '@ynap/notification'
import track from 'react-tracking'
import { packagingOptionsContinue, packagingOptionsChange } from '../analytics/index'
import PackagingChoices from '../PackagingChoices/PackagingChoices'

const bem = new BEMHelper('PackagingOptionsForm')

const propTypes = {
    form: PropTypes.string.isRequired,
    onSubmit: PropTypes.func.isRequired,
    error: PropTypes.string,
    submitting: PropTypes.bool,
    handleSubmit: PropTypes.func,
    className: PropTypes.string,
    isMoreThanOnePackagingOptions: PropTypes.bool,
    packagingOptions: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string.isRequired,
            description: PropTypes.string,
            price: PropTypes.string,
            selected: PropTypes.bool,
        })
    ).isRequired,
    messages: PropTypes.shape({
        packagingOptions: PropTypes.shape({
            label: PropTypes.shape({
                basic: PropTypes.func.isRequired,
                signature: PropTypes.func.isRequired,
            }),
            description: PropTypes.func.isRequired,
            subtitle: PropTypes.string,
            subDescription: PropTypes.string,
            basicOnly: PropTypes.string,
            submit: PropTypes.func.isRequired,
            recommendation: PropTypes.string,
            ecoResponsible: PropTypes.string,
        }).isRequired,
    }).isRequired,
    config: PropTypes.object.isRequired,
    locale: PropTypes.object.isRequired,
    checkoutAnalytics: PropTypes.object.isRequired,
}

const defaultProps = {
    submitting: false,
    isMoreThanOnePackagingOptions: false,
}

const MAX_LENGTH_GIFT_MESSAGE = 180
const MAX_LINE_FEED = 0
const normalizeGiftMessageConfigured = (message, prevMessage) =>
    normalizeGiftmessage(message, prevMessage, { maxLength: MAX_LENGTH_GIFT_MESSAGE, maxLinefeed: MAX_LINE_FEED })

@track()
export class PackagingOptionsForm extends PureComponent {
    constructor(props) {
        super(props)
        this.state = {
            sustainabilityOpen: false,
        }
    }

    setSustainabilityOpen = (sustainabilityOpen) => this.setState({ sustainabilityOpen })

    @track(packagingOptionsContinue)
    onContinue() {}

    render() {
        const {
            form,
            messages,
            submitting,
            handleSubmit,
            className,
            packagingOptions,
            error,
            tracking,
            firstTimeUser,
            isGift = 'N',
            giftMessage = '',
            config,
            isMoreThanOnePackagingOptions,
            locale,
            checkoutAnalytics,
        } = this.props
        const {
            notifications: { packagingInfo },
        } = messages

        const basicOnly = config.head?.flags?.basicPackagingOnly
        const sustainabilityEnabled = config?.head?.flags?.enableSustainability
        const giftMessageEnabled = !!config?.head?.flags?.enableGiftMessage
        const country = locale.country.toUpperCase()
        const giftMessageCountryEnabled = config?.head?.giftMessageCountryList ? config?.head?.giftMessageCountryList?.includes(country) : true

        const showGiftMessage = giftMessageEnabled && giftMessageCountryEnabled

        const orderedBySustainability = packagingOptions.sort((a, b) => {
            if (a.name === 'Basic') {
                return -1
            }
            return 0
        })
        const reorderedPackagingOptions = sustainabilityEnabled ? [...orderedBySustainability] : packagingOptions
        const remainingChars = MAX_LENGTH_GIFT_MESSAGE - giftMessage.length
        const showSustainabilityOptions = sustainabilityEnabled && isMoreThanOnePackagingOptions

        return (
            <form id={form} className={bem(null, { submitting }, className)} onSubmit={handleSubmit(submit)}>
                {firstTimeUser && !basicOnly && (
                    <Notification className={bem('packagingInfo')}>
                        <div className={bem('packagingInfo-title')}>{packagingInfo.title()}</div>
                        <div className={bem('packagingInfo-description')}>{packagingInfo.description()}</div>
                    </Notification>
                )}
                {basicOnly && packagingInfo.basicOnly && (
                    <Notification className={bem('packagingInfo')}>
                        <div className={bem('packagingInfo-title')}>{packagingInfo.title()}</div>
                        <div className={bem('packagingInfo-description')}>{packagingInfo.basicOnly()}</div>
                    </Notification>
                )}
                <Field component={RadioButtonGroup} name="packagingOption" divided removeFirstDivider>
                    {reorderedPackagingOptions.map((option) => {
                        const isBasic = option.key === 'basic'
                        return (
                            <RadioButtonWithPrice
                                className={bem('radioButtons', {}, [
                                    'packagingOption',
                                    isBasic && sustainabilityEnabled && isMoreThanOnePackagingOptions ? 'withTag' : '',
                                ])}
                                onClick={() => tracking.trackEvent(packagingOptionsChange(checkoutAnalytics, option.name))}
                                key={option.key}
                                value={option.name}
                                label={messages.packagingOptions?.label?.[option.key]?.() || option?.description}
                                description={messages.packagingOptions.description[option.key]?.()}
                                finalShippingPrice={option.finalShippingPrice}
                                originalShippingPrice={option.originalShippingPrice}
                                img={bem(option.key)}
                            >
                                {isBasic && sustainabilityEnabled && isMoreThanOnePackagingOptions && (
                                    <span className={bem('tag')}>{messages.packagingOptions?.recommendation()}</span>
                                )}
                            </RadioButtonWithPrice>
                        )
                    })}
                </Field>

                {showSustainabilityOptions && (
                    <>
                        <div className={bem('subDescription')}>{messages.packagingOptions?.subDescription()}</div>
                        <Button component="a" className={bem('learnMore')} onClick={() => this.setSustainabilityOpen(true)}>
                            {messages.packagingOptions?.learnMore()}
                        </Button>
                        {this.state.sustainabilityOpen && <PackagingChoices onClose={this.setSustainabilityOpen} config={config} locale={locale} />}
                    </>
                )}

                {showSustainabilityOptions && showGiftMessage && <div className={bem('divider')} />}

                {showGiftMessage && (
                    <div className={bem('addGiftMessage')}>
                        <Field
                            component={Checkbox}
                            className={bem('addGiftMessage', 'checkbox')}
                            name="isGift"
                            format={(value) => value === 'Y'}
                            normalize={(value) => (value ? 'Y' : 'N')}
                        >
                            {messages.packagingOptions?.addGiftCheckbox()}
                        </Field>

                        {isGift === 'Y' && (
                            <>
                                <Field
                                    label={messages.packagingOptions?.addGiftLabel?.()}
                                    className={bem('giftMessageField')}
                                    name="giftMessage"
                                    normalize={normalizeGiftMessageConfigured}
                                    placeholder={messages.packagingOptions?.addGiftPlaceholder?.()}
                                    component="textarea"
                                />
                                <div className={bem('addGiftRemainingText')}>
                                    {messages.packagingOptions?.addGiftRemainingText?.({ amount: remainingChars })}
                                </div>
                                <div className={bem('giftMessageWarning')}>{messages.packagingOptions?.giftMessageWarning?.()}</div>
                            </>
                        )}
                    </div>
                )}

                <Tooltip className={bem('buttonWrapper')} type="error" message={error} defaultOpen={!submitting && !!error} key={submitting || error}>
                    <SimpleAsyncButton
                        copy={messages.packagingOptions.submit()}
                        type="primary"
                        className={bem('submit')}
                        loading={submitting}
                        onClick={() => this.onContinue()}
                    />
                </Tooltip>
            </form>
        )
    }
}

PackagingOptionsForm.propTypes = propTypes
PackagingOptionsForm.defaultProps = defaultProps

export const PackagingOptionsFormWithValues = formValues({ packagingOptionValue: 'packagingOption', isGift: 'isGift', giftMessage: 'giftMessage' })(
    PackagingOptionsForm
)

export default reduxForm({ form: 'packagingOptions', collateErrors })(PackagingOptionsFormWithValues)
